import { clampNumber } from "@/utils/clamp";

export type FloatingPanelGeometry = {
    x: number;
    y: number;
    width: number;
    height: number;
};

/** The four corners a floating panel can be dragged by. */
export const FLOATING_PANEL_RESIZE_CORNERS = ["nw", "ne", "se", "sw"] as const;

export type FloatingPanelResizeCorner = (typeof FLOATING_PANEL_RESIZE_CORNERS)[number];

export type FloatingPanelBounds = {
    viewportWidth: number;
    viewportHeight: number;
    minWidth: number;
    minHeight: number;
    margin?: number;
};

export function parseFloatingPanelGeometry(value: unknown): FloatingPanelGeometry | null {
    if (typeof value !== "object" || value === null || Array.isArray(value)) {
        return null;
    }

    const candidate = value as Record<string, unknown>;
    if (
        typeof candidate.x !== "number" ||
        !Number.isFinite(candidate.x) ||
        typeof candidate.y !== "number" ||
        !Number.isFinite(candidate.y) ||
        typeof candidate.width !== "number" ||
        !Number.isFinite(candidate.width) ||
        typeof candidate.height !== "number" ||
        !Number.isFinite(candidate.height)
    ) {
        return null;
    }

    return {
        x: candidate.x,
        y: candidate.y,
        width: candidate.width,
        height: candidate.height,
    };
}

export function clampFloatingPanelGeometry(geometry: FloatingPanelGeometry, bounds: FloatingPanelBounds): FloatingPanelGeometry {
    const margin = bounds.margin ?? 16;
    const availableWidth = Math.max(0, bounds.viewportWidth - margin * 2);
    const availableHeight = Math.max(0, bounds.viewportHeight - margin * 2);
    const minWidth = Math.min(bounds.minWidth, availableWidth);
    const minHeight = Math.min(bounds.minHeight, availableHeight);
    const width = clampNumber(geometry.width, minWidth, availableWidth);
    const height = clampNumber(geometry.height, minHeight, availableHeight);
    const x = clampNumber(geometry.x, margin, Math.max(margin, bounds.viewportWidth - width - margin));
    const y = clampNumber(geometry.y, margin, Math.max(margin, bounds.viewportHeight - height - margin));

    return { x, y, width, height };
}

export function resizeFloatingPanelGeometry(
    geometry: FloatingPanelGeometry,
    corner: FloatingPanelResizeCorner,
    deltaX: number,
    deltaY: number,
    bounds: FloatingPanelBounds,
): FloatingPanelGeometry {
    const margin = bounds.margin ?? 16;
    const origin = clampFloatingPanelGeometry(geometry, bounds);
    const minWidth = Math.min(bounds.minWidth, Math.max(0, bounds.viewportWidth - margin * 2));
    const minHeight = Math.min(bounds.minHeight, Math.max(0, bounds.viewportHeight - margin * 2));
    let left = origin.x;
    let right = origin.x + origin.width;
    let top = origin.y;
    let bottom = origin.y + origin.height;

    if (corner.includes("w")) {
        left = clampNumber(origin.x + deltaX, margin, right - minWidth);
    } else {
        right = clampNumber(right + deltaX, left + minWidth, bounds.viewportWidth - margin);
    }

    if (corner.includes("n")) {
        top = clampNumber(origin.y + deltaY, margin, bottom - minHeight);
    } else {
        bottom = clampNumber(bottom + deltaY, top + minHeight, bounds.viewportHeight - margin);
    }

    return { x: left, y: top, width: right - left, height: bottom - top };
}
